import type { Database } from '../supabase/types';

import { createClient } from '@supabase/supabase-js';
import { ref } from 'vue';
import { z } from 'zod';

export interface UserMetadata {
  passwordSetup?: boolean;
}

// Process the #access_token and #error before vue-router "cleans up",
// breaking supabase.auth token processing later on
let indexSupabaseHash = window.location.hash.indexOf('#access_token');
if (indexSupabaseHash < 0)
  indexSupabaseHash = window.location.hash.indexOf('#error');
const routerHash = window.location.hash.substring(
  0,
  indexSupabaseHash >= 0 ? indexSupabaseHash : undefined,
);

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

type Session = Awaited<
  ReturnType<typeof supabase.auth.getSession>
>['data']['session'];

export const session = ref<Session>();
export const setPasswordRequested = ref<'initial' | 'reset' | false>(false);

// must be registered immediately before returning to the event loop because
// otherwise changes triggered by the #access_token, e.g. PASSWORD_RECOVERY,
// would be missed if the **async** supabase.auth._initialize() is processed
// before the callback is registered.
supabase.auth.onAuthStateChange(async (_event, _session) => {
  console.log('supabase.auth.onAuthStateChange', _event, _session);
  if (JSON.stringify(_session) !== JSON.stringify(session.value))
    // prevent extra updates (e.g. for the Orthanc JWT)
    session.value = _session;
  if (_event === 'PASSWORD_RECOVERY') setPasswordRequested.value = 'reset';

  const userMetaData = session.value?.user.user_metadata as UserMetadata | null;
  if (userMetaData && !userMetaData.passwordSetup) {
    setPasswordRequested.value = 'initial';
    const response = await supabase.auth.updateUser({ data: { passwordSetup: true } });
    if (response.error) console.error(response.error.message);
  }
});

/**
 * Finalize supabase init
 *
 * Shall be called from the router initialization before it is instantiated
 * and mangles the hash parts of the window.location. This function also removes
 * the supabase hash parts and reinstates the vue-router hash parts which are
 * otherwise cleared out by supabase.auth.initialize().
 *
 * It's all a bit upstream messy indeed.
 */
export async function init() {
  // this waits for the async init to finish:
  const { error } = await supabase.auth.initialize();
  if (error) console.error(error.message);
  window.location.hash = routerHash;
  console.log('supabase.auth.initialize', session.value);
}

export type ServerMode = 'bootstrapping' | 'setup' | 'operation';

/**
 * Retrieve  the server mode from the supabase database
 *
 * @returns A promise that resolves to the server mode as a string (either 'bootstrapping', 'setup', or 'operation').
 * @throws An error if the RPC call to retrieve the server mode fails.
 *
 * Note: If the server mode is determined to be 'operation', the result will
 * be cached and not re-queried for this session in order to avoid excessive
 * checks.
 */
export const getServerMode = (() => {
  let cachedMode: null | ServerMode = null;
  return async (): Promise<ServerMode> => {
    if (cachedMode !== null) return cachedMode;
    const { data: mode, error } = await supabase.rpc('server_mode');
    if (error) throw error;
    // once in operation, do not check again (for this session)
    if (mode === 'operation') cachedMode = mode;
    return mode as ServerMode;
  };
})();

const DicomArchiveConfigItemSchema = z.object({
  fn: z.string().min(1, 'Name is required'),
  url: z.string().url('Invalid DICOM Web root URL'),
});

type DicomArchiveConfigItem = z.infer<typeof DicomArchiveConfigItemSchema>;

export type DicomArchiveConfig = DicomArchiveConfigItem & {
  id: string;
};

export async function getDicomArchiveConfig(): Promise<DicomArchiveConfig[]> {
  try {
    const { data, error } = await supabase.from('dicom_archive').select(`
      id,
      card
      `);

    if (error) throw error;
    if (!data) return [];

    return data
      .map((c) => {
        const parsedCard = DicomArchiveConfigItemSchema.safeParse(c.card);
        if (!parsedCard.success) {
          console.error('Invalid Orthanc config item:', parsedCard.error);
          return null;
        }
        // parsedCard.data.url = parsedCard.data.url.replace('https://ocg19.fly.dev', 'http://localhost');
        return {
          id: c.id,
          ...parsedCard.data,
        };
      })
      .filter(Boolean) as DicomArchiveConfig[];
  }
  catch (error) {
    console.error('Error fetching Orthanc config:', error);
    return [];
  }
}
